<template>
  <div class="reader-upgrades">
    <div class="heading">Choose your readers</div>
    <div class="reader-instructions">
      Choose one or more of the options below. No matter which options you
      select, all reviews are posted directly to Amazon by your readers.
    </div>
    <div class="upgrade-card">
      <div class="upgrade-item upgrade-item-standard">
        <div
          class="upgrade-checkbox"
          :class="{ 'upgrade-checkbox-checked': isStandard }"
          @click="clickStandard"
        >
          <i class="material-icons checkbox-icon">{{
            isStandard ? "check_box" : "check_box_outline_blank"
          }}</i>
        </div>
        <div class="upgrade-label">
          <span @click="clickStandard" style="cursor: pointer">Standard</span>
          <div class="upgrade-more">
            <p>
              All readers can review your book using a free copy that you
              <br />provide to read.
            </p>
          </div>
          <div class="upgrade-manuscript-required" v-if="!isStandard">
            Requires a PDF or ePub copy of your book
          </div>
          <!-- <div class="upgrade-manuscript-required-more" v-if="!isStandard">
            If you prefer to not provide a file of your manuscript,
            <br />choose one or both of the options below.
          </div>-->
          <div class="upgrade-expanded" v-if="isStandard">
            <div v-if="manuscriptStatus === 'Completed'">
              <div class="manuscript-display">
                <div class="manuscript-done">
                  <i class="material-icons manuscript-done-icon">cloud_done</i>
                </div>
                <div class="manuscript-done-filetype">
                  {{ manuscriptFileUrl.split(".").pop() }}
                </div>
              </div>
              <div class="remove-file" @click="removeManuscript">
                Remove File
              </div>
              <div class="manuscript-done-more">
                Great! You're all set here. If you'd like readers to use Kindle
                Unlimited
                <span v-if="user.memberType === 'paid'"> or Verified Purchases</span> to read
                your book too, you can select those options also. Otherwise, you
                can click the blue 'Continue' button below.
              </div>
            </div>
            <div v-else>
              <vue-dropzone
                ref="manuscriptDropzone"
                id="dropzone"
                :options="dropzoneManuscriptOptions"
                v-on:vdropzone-complete="manuscriptComplete"
              ></vue-dropzone>
              <div class="upload-instructions">
                <span style="font-weight: 600"
                  >Accepted file types: .epub, .pdf</span
                >
                <br />Your book copy will be securely encrypted and only made
                accessible to readers who have already agreed to review your
                book.
                <br />
                <br />
                <span style="font-weight: 600">Having trouble?</span> Click the
                yellow icon in the bottom-left corner of your screen or email
                support@pubby.co for help.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="upgrade-card">
      <div class="upgrade-item upgrade-item-kindle">
        <div
          class="upgrade-checkbox"
          :class="{ 'upgrade-checkbox-checked': isKindle }"
          @click="clickKindle"
        >
          <i class="material-icons checkbox-icon">{{
            isKindle ? "check_box" : "check_box_outline_blank"
          }}</i>
        </div>
        <div class="upgrade-label">
          <span @click="clickKindle" style="cursor: pointer"
            >Kindle Unlimited</span
          >
          <div class="upgrade-more">
            <p>
              Readers with a Kindle Unlimited subscription can review your book.
              Your readers will read your book using Kindle Unlimited.
            </p>
          </div>
          <div class="upgrade-kindle-required" v-if="!isKindlePurchased">
            <div>Requires a one-time payment of $9.99</div>
            <div>
              <i @click="openKindle" class="material-icons help-icon-i"
                >help_outline</i
              >
            </div>
          </div>
          <div class="upgrade-kindle-required" v-else>
            <span v-if="user.memberType === 'paid'"
              >Requires your book to be free for KU subscribers.</span
            >
            <span v-if="user.memberType === 'free'"
              >Requires your book to be free for KU subscribers.</span
            >
          </div>

          <div
            class="upgrade-expanded"
            v-if="(isKindle || isKindleOpen) && !isKindlePurchased"
          >
            <ul class="upgrade-more-list">
              <li class="upgrade-more-item">
                Your book must be free to read with Kindle Unlimited.
              </li>
              <li class="upgrade-more-item">
                After purchasing this option once, it can be used for all of
                your books and does not expire.
              </li>
              <!-- <li class="upgrade-more-item">
                Books are typically approved in 2-4 hours. If your book is not approved
                for any reason, you will not be charged.
              </li>-->
              <li class="upgrade-more-item">
                This purchase is non-refundable.
              </li>
            </ul>
            <div class="payment-buttons">
              <div
                @click="clickKindlePayNow"
                class="payment-button payment-button-now"
              >
                Pay Now
              </div>
              <div
                @click="clickKindlePayLater"
                class="payment-button payment-button-later"
              >
                Pay When Your Book Is Approved
              </div>
            </div>
            <div class="clearfix"></div>
            <div class="payment-notice">
              You will be asked to confirm your payment option.
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="upgrade-card" v-if="user.memberType === 'paid'">
      <div class="upgrade-item upgrade-item-verified">
        <div
          class="upgrade-checkbox"
          :class="{ 'upgrade-checkbox-checked': isVerified }"
          @click="clickVerified"
        >
          <i class="material-icons checkbox-icon">{{
            isVerified ? "check_box" : "check_box_outline_blank"
          }}</i>
        </div>
        <div class="upgrade-label">
          <span @click="clickVerified" style="cursor: pointer"
            >Verified Purchases</span
          >
          <div class="upgrade-more">
            <p>
              Readers who agree to purchase your book on Amazon can review
              your book. Your readers will read your book using Kindle.
            </p>
          </div>
          <div class="upgrade-verified-required" v-if="!isVerifiedPurchased">
            <div>Requires a one-time payment of $14.99</div>
            <div>
              <i @click="openVerified" class="material-icons help-icon-i"
                >help_outline</i
              >
            </div>
          </div>
          <div class="upgrade-verified-required" v-else>
            You have lifetime access to Verified Purchases readers
          </div>
          <div
            class="upgrade-expanded"
            v-if="(isVerified || isVerifiedOpen) && !isVerifiedPurchased"
          >
            <ul class="upgrade-more-list">
              <li class="upgrade-more-item">
                Your book must have a Kindle edition that is priced between $.99
                and $4.99 or availible for a free promotion.
              </li>
              <li class="upgrade-more-item">
                After purchasing this option once, it can be used for all of
                your books and does not expire.
              </li>
              <li class="upgrade-more-item">
                This purchase is non-refundable.
              </li>
            </ul>
            <div class="payment-buttons">
              <div
                @click="clickVerifiedPayNow"
                class="payment-button payment-button-now"
              >
                Pay Now
              </div>
              <div
                @click="clickVerifiedPayLater"
                class="payment-button payment-button-later-verified"
              >
                Pay When Your Book Is Approved
              </div>
            </div>
            <div class="clearfix"></div>
            <div class="payment-notice">
              You will be asked to confirm your payment option.
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="buttons">
      <div
        v-if="!isCompleted"
        class="continue-button action"
        @click="clickContinue"
      >
        Continue
      </div>
    </div>

    <div
      class="modal-container modal-kindle"
      v-if="isKindlePayNowOpen && paymentMethod"
    >
      <div class="payment-confirm">
        <div class="payment-heading">
          <span style="font-weight: 600">Kindle Unlimited Upgrade</span>
          <br />
          <span style="font-size: 20pt">Confirm your payment</span>
        </div>
        <div class="payment-line">
          Your
          {{ paymentMethod.card_brand }} card ending in
          <span style="font-weight: 600">{{
            paymentMethod.card_last_four
          }}</span>
          will be charged <span style="font-weight: 600">$9.99</span>. <br />If
          for any reason your book cannot be approved, your payment will be
          refunded.
        </div>
        <div
          @click="confirmKindlePayNow"
          class="payment-button payment-button-now"
        >
          Pay $9.99
        </div>
        <div
          @click="cancelKindlePayNow"
          class="payment-button payment-button-cancel"
        >
          Cancel
        </div>
      </div>
    </div>

    <div
      class="modal-container modal-kindle modal-container-later"
      v-if="isKindlePayLaterOpen && paymentMethod"
    >
      <div class="payment-confirm">
        <div class="payment-heading">
          <span style="font-weight: 600">Kindle Unlimited Upgrade</span>
          <br />
          <span style="font-size: 20pt">Confirm your payment for later</span>
        </div>
        <div class="payment-line">
          Your
          {{ paymentMethod.card_brand }} card ending in
          <span style="font-weight: 600">{{
            paymentMethod.card_last_four
          }}</span>
          will be charged <span style="font-weight: 600">$9.99</span> when your
          book is approved.
          <p style="padding-top: 12px; padding-bottom: 0; margin-bottom: 0">
            Books are typically approved within 4 hours. If for any reason your
            book cannot be approved, your payment will not be made.
          </p>
        </div>
        <div
          @click="confirmKindlePayLater"
          class="payment-button payment-button-later"
        >
          Pay $9.99 When My Book Is Approved
        </div>
        <div
          @click="cancelKindlePayLater"
          class="payment-button payment-button-cancel"
        >
          Cancel
        </div>
      </div>
    </div>

    <div
      class="modal-container modal-verified"
      v-if="isVerifiedPayNowOpen && paymentMethod"
    >
      <div class="payment-confirm">
        <div class="payment-heading payment-heading-verified">
          <span style="font-weight: 600">Verified Purchases Upgrade</span>
          <br />
          <span style="font-size: 20pt">Confirm your payment</span>
        </div>
        <div class="payment-line">
          Your
          {{ paymentMethod.card_brand }} card ending in
          <span style="font-weight: 600">{{
            paymentMethod.card_last_four
          }}</span>
          will be charged <span style="font-weight: 600">$14.99</span>. <br />If
          for any reason your book cannot be approved, your payment will be
          refunded.
        </div>
        <div
          @click="confirmVerifiedPayNow"
          class="payment-button payment-button-now"
        >
          Pay $14.99
        </div>
        <div
          @click="cancelVerifiedPayNow"
          class="payment-button payment-button-cancel"
        >
          Cancel
        </div>
      </div>
    </div>

    <div
      class="modal-container modal-verified modal-container-later"
      v-if="isVerifiedPayLaterOpen && paymentMethod"
    >
      <div class="payment-confirm">
        <div class="payment-heading payment-heading-verified">
          <span style="font-weight: 600">Verified Purchases Upgrade</span>
          <br />
          <span style="font-size: 20pt">Confirm your payment for later</span>
        </div>
        <div class="payment-line">
          Your
          {{ paymentMethod.card_brand }} card ending in
          <span style="font-weight: 600">{{
            paymentMethod.card_last_four
          }}</span>
          will be charged <span style="font-weight: 600">$14.99</span> when your
          book is approved.
          <p style="padding-top: 12px; padding-bottom: 0; margin-bottom: 0">
            Books are typically approved within 4 hours. If for any reason your
            book cannot be approved, your payment will not be made.
          </p>
        </div>
        <div
          @click="confirmVerifiedPayLater"
          class="
            payment-button payment-button-later payment-button-later-verified
          "
        >
          Pay $14.99 When My Book Is Approved
        </div>
        <div
          @click="cancelVerifiedPayLater"
          class="payment-button payment-button-cancel"
        >
          Cancel
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Back from "../../modules/Back";
import { mapState } from "vuex";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

export default {
  name: "ReaderUpgrades",
  props: [
    "book",
    "onComplete",
    "onError",
    "onManuscriptComplete",
    "onManuscriptRemove",
    "savedManuscriptUrl",
  ],
  components: {
    vueDropzone: vue2Dropzone,
  },
  data: function () {
    return {
      isCompleted: false,
      isDirty: false,
      isStandard: false,
      isKindlePurchased: true,
      isVerifiedPurchased: false,
      isKindle: false,
      isVerified: false,
      isKindleOpen: false,
      isKindlePayNowOpen: false,
      isKindlePayLaterOpen: false,
      isVerifiedOpen: false,
      isVerifiedPayNowOpen: false,
      isVerifiedPayLaterOpen: false,
      manuscriptStatus: null,
      manuscriptFileUrl: null,
      paymentMethod: null,
    };
  },
  computed: {
    ...mapState({
      user: "user",
    }),
  },
  watch: {
    user(newValue, oldValue) {
      const user = newValue;

      if (user && user.is_kindle_readers_upgrade) {
        this.isKindlePurchased = true;
      }
      if (user && user.is_verified_readers_upgrade) {
        this.isVerifiedPurchased = true;
      }
    },
  },
  methods: {
    clickStandard() {
      this.isStandard = !this.isStandard;
      this.optionsChanged();
    },
    clickKindle() {
      this.isKindle = !this.isKindle;
      this.optionsChanged();
    },
    clickVerified() {
      this.isVerified = !this.isVerified;
      this.optionsChanged();
    },
    openKindle() {
      this.isKindleOpen = !this.isKindleOpen;
    },
    openVerified() {
      this.isVerifiedOpen = !this.isVerifiedOpen;
    },
    clickContinue() {
      if (!this.isStandard && !this.isKindle && !this.isVerified) {
        this.$toasted.show(
          "Please choose one or more of the options above before continuing.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 4000,
          }
        );
        this.isCompleted = false;
        this.onError();
        return;
      }

      if (this.isStandard && !this.manuscriptFileUrl) {
        this.$toasted.show(
          "Please upload a PDF or ePub copy of your book before continuing. Or, uncheck the Standard option.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 4000,
          }
        );
        this.isCompleted = false;
        this.onError();
        return;
      }

      if (this.isKindle && !this.isKindlePurchased) {
        this.$toasted.show(
          "Please select a payment option for Kindle Unlimited before continuing.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 4000,
          }
        );
        this.isCompleted = false;
        this.onError();
        return;
      }

      if (this.isVerified && !this.isVerifiedPurchased) {
        this.$toasted.show(
          "Please select a payment option for Verified Purchases before continuing.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 4000,
          }
        );
        this.isCompleted = false;
        this.onError();
        return;
      }

      this.isCompleted = true;

      this.onComplete(this.isStandard, this.isKindle, this.isVerified);
    },
    async clickKindlePayNow() {
      this.isKindlePayNowOpen = true;

      if (!this.paymentMethod) {
        this.getUserPaymentMethod();
      }
    },
    clickKindlePayLater() {
      this.isKindlePayLaterOpen = true;

      if (!this.paymentMethod) {
        this.getUserPaymentMethod();
      }
    },
    confirmKindlePayNow() {
      const response = Back.userPlaceOrder("kindle-readers-upgrade-sku-1");

      if (!response) {
        this.$toasted.show(
          "Your payment could not be completed. Please contact our support team, or try again later.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 4000,
          }
        );
        return;
      }

      this.$toasted.show(
        "Your payment has been completed. You now have access to Kindle Unlimited readers for all of your books!",
        {
          icon: "check_circle",
          position: "bottom-center",
          duration: 3000,
        }
      );

      this.isKindlePayNowOpen = false;
      this.isKindlePurchased = true;

      // console.log("confirmKindlePayNow", response);
    },
    confirmKindlePayLater() {
      const response = Back.userPlaceOrder(
        "kindle-readers-upgrade-sku-later-1"
      );

      if (!response) {
        this.$toasted.show(
          "Your payment could not be completed. Please contact our support team, or try again later.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 4000,
          }
        );
        return;
      }

      this.$toasted.show(
        "Your payment has been confirmed for when your book is approved. You now have access to Kindle Unlimited readers for all of your books!",
        {
          icon: "check_circle",
          position: "bottom-center",
          duration: 3000,
        }
      );

      this.isKindlePayLaterOpen = false;
      this.isKindlePurchased = true;

      // console.log("confirmKindlePayLater", response);
    },
    cancelKindlePayNow() {
      this.isKindlePayNowOpen = false;
    },
    cancelKindlePayLater() {
      this.isKindlePayLaterOpen = false;
    },

    async clickVerifiedPayNow() {
      this.isVerifiedPayNowOpen = true;

      if (!this.paymentMethod) {
        this.getUserPaymentMethod();
      }
    },
    clickVerifiedPayLater() {
      this.isVerifiedPayLaterOpen = true;

      if (!this.paymentMethod) {
        this.getUserPaymentMethod();
      }
    },
    confirmVerifiedPayNow() {
      const response = Back.userPlaceOrder("verified-readers-upgrade-sku-1");

      if (!response) {
        this.$toasted.show(
          "Your payment could not be completed. Please contact our support team, or try again later.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 4000,
          }
        );
        return;
      }

      this.$toasted.show(
        "Your payment has been completed. You now have access to Verified Unlimited readers for all of your books!",
        {
          icon: "check_circle",
          position: "bottom-center",
          duration: 3000,
        }
      );

      this.isVerifiedPayNowOpen = false;
      this.isVerifiedPurchased = true;

      // console.log("confirmVerifiedPayNow", response);
    },
    confirmVerifiedPayLater() {
      const response = Back.userPlaceOrder(
        "verified-readers-upgrade-sku-later-1"
      );

      if (!response) {
        this.$toasted.show(
          "Your payment could not be completed. Please contact our support team, or try again later.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 4000,
          }
        );
        return;
      }

      this.$toasted.show(
        "Your payment has been confirmed for when your book is approved. You now have access to Verified Unlimited readers for all of your books!",
        {
          icon: "check_circle",
          position: "bottom-center",
          duration: 3000,
        }
      );

      this.isVerifiedPayLaterOpen = false;
      this.isVerifiedPurchased = true;

      // console.log("confirmVerifiedPayLater", response);
    },
    cancelVerifiedPayNow() {
      this.isVerifiedPayNowOpen = false;
    },
    cancelVerifiedPayLater() {
      this.isVerifiedPayLaterOpen = false;
    },

    isStandardValid() {},
    optionsChanged() {
      this.isCompleted = false;
      this.onError();

      if (this.book.status !== "Editing") {
        this.clickContinue();
      }
    },
    manuscriptComplete(response) {
      const responseJson = response.xhr.responseText;
      const responseBody = JSON.parse(responseJson);

      if (!responseBody || !responseBody.success) {
        this.manuscriptStatus = "Error";
        return;
      }

      this.manuscriptStatus = "Completed";
      this.manuscriptFileUrl = responseBody.data.file_url;
      this.onManuscriptComplete(this.manuscriptFileUrl);
      //this.decideSectionStatus();
      //this.save();

      if (this.book.status !== "Editing") {
        this.clickContinue();
      }
    },
    removeManuscript() {
      this.manuscriptFileUrl = null;
      this.manuscriptStatus = null;
      this.onManuscriptRemove();
    },
    async getUserPaymentMethod() {
      const response = await Back.userPaymentMethod();

      if (!response) {
        this.$toasted.show(
          "Your payment method could not be found. Please contact our support team or try again later.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 4000,
          }
        );
      }

      this.paymentMethod = response;

      // console.log("getUserPaymentMethod", response);
    },
  },
  mounted() {
    this.dropzoneManuscriptOptions = {
      url: Back.manuscriptUploadUrl(this.book),
      acceptedFiles: ".pdf,.epub",
      thumbnailWidth: 150,
      maxFilesize: 200,
      timeout: 90000,
      headers: {},
    };

    this.isStandard = this.book.is_standard_readers;
    this.isKindle = this.book.is_kindle_readers;
    this.isVerified = this.book.is_verified_readers;

    if (this.savedManuscriptUrl) {
      this.manuscriptFileUrl = this.savedManuscriptUrl;
      this.manuscriptStatus = "Completed";
    }

    if (this.user && this.user.is_kindle_readers_upgrade) {
      this.isKindlePurchased = true;
    }
    if (this.user && this.user.is_verified_readers_upgrade) {
      this.isVerifiedPurchased = true;
    }

    if (this.isStandard || this.isKindle || this.isVerified) {
      this.isCompleted = true;
    }
  },
};
</script>

<style lang="scss" scoped>
.reader-upgrades {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px 0 10px 0;
}

.reader-instructions {
  text-align: center;
  padding: 20px 40px 0px 40px;
  font-size: 13pt;
}

.upgrade-card {
  margin: 20px;
  padding: 10px 40px 12px 4px;
  box-shadow: 0px 1px 0.5rem rgba(116, 75, 75, 0.25);
  border-radius: 8px;
}

.upgrade-item {
  width: 100%;
  padding: 12px 0 12px 0;
  display: flex;

  .upgrade-checkbox {
    margin: 0px 0px 4px 20px;
    width: 28px;
    min-width: 28px;
    height: 32px;
    background: #fff;
    border: 0px solid #eee;
    border-radius: 4px;
  }

  // .checkbox-icon {
  //   color: #ccc;
  // }

  .upgrade-label {
    padding-left: 20px;
    padding-top: 2px;
    font-size: 15pt;
    font-weight: 600;
    color: $color-blue;
  }

  .upgrade-more {
    padding-left: 1px;
    padding-top: 6px;
    font-size: 13pt;
    font-weight: 500;
    line-height: 1.35em;
    color: #111;

    p {
      font-size: 12.5pt;
      padding: 0;
      margin-bottom: 10px;
    }
  }
}

.upgrade-manuscript-required {
  padding-left: 1px;
  padding-top: 6px;
  font-size: 13pt;
  font-weight: 600;
  line-height: 1.35em;
  color: $color-blue;
}
.upgrade-manuscript-required-more {
  padding: 6px 50px 0px 0px;
  font-size: 12pt;
  font-weight: 400;
  line-height: 1.35em;
  color: #777;
}
.upgrade-kindle-required {
  display: flex;
  padding-left: 1px;
  padding-top: 6px;
  font-size: 13pt;
  font-weight: 600;
  line-height: 1.35em;
  color: darken(#e9a23b, 5%);
}
.upgrade-verified-required {
  display: flex;
  padding-left: 1px;
  padding-top: 6px;
  font-size: 13pt;
  font-weight: 600;
  line-height: 1.35em;
  color: darken($color-snaps, 4%);
}

.upgrade-more-list {
  padding: 0px 20px 0 30px;
  .upgrade-more-item {
    padding: 5px 0px;
    color: #111;
    font-size: 12pt;
    font-weight: 400;
  }
}

.upgrade-item-standard {
  .upgrade-label {
    color: lighten($color-blue, 1%);
  }
  .checkbox-icon {
    color: lighten($color-blue, 17%);
  }
  .checkbox-icon:hover {
    color: lighten($color-blue, 8%);
    cursor: pointer;
  }
  .upgrade-checkbox-checked .checkbox-icon {
    color: lighten($color-blue, 5%);
  }
  .upgrade-checkbox-checked .checkbox-icon:hover {
    color: darken($color-blue, 0%);
  }
}
.upgrade-item-verified {
  .upgrade-label {
    color: lighten($color-snaps, 0%);
  }
  .checkbox-icon {
    color: lighten($color-snaps, 13%);
  }
  .checkbox-icon:hover {
    color: lighten($color-snaps, 6%);
    cursor: pointer;
  }
  .upgrade-checkbox-checked .checkbox-icon {
    color: lighten($color-snaps, 6%);
  }
  .upgrade-checkbox-checked .checkbox-icon:hover {
    color: lighten($color-snaps, 1%);
  }
}
.upgrade-item-kindle {
  .upgrade-label {
    color: darken(#e9a23b, 5%);
  }
  .checkbox-icon {
    color: lighten(#e9a23b, 12%);
  }
  .checkbox-icon:hover {
    color: lighten(#e9a23b, 6%);
    cursor: pointer;
  }
  .upgrade-checkbox-checked .checkbox-icon {
    color: #e9a23b;
  }
  .upgrade-checkbox-checked .checkbox-icon:hover {
    color: darken(#e9a23b, 5%);
  }
}

.payment-buttons {
  margin: 20px 4px;
}
.payment-button {
  float: left;
  margin-right: 8px;
  font-size: 13.5pt;
  font-weight: 600;
  background: darken($color-blue, 4%);
  background: linear-gradient(
    0deg,
    $color-blue 0%,
    lighten($color-blue, 13%) 100%
  );
  border-radius: 4px;
  border: 2px solid darken($color-blue, 4%);
  border-bottom: 2px solid darken($color-blue, 7%);
  color: #fff;
  padding: 6px 18px;
  padding-top: 5px;
  box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08);
  text-shadow: 2px 2px 1px rgba(17, 17, 17, 0.25);
  cursor: pointer;
}
.payment-button:hover {
  background: linear-gradient(
    0deg,
    darken($color-blue, 8%) 0%,
    lighten($color-blue, 5%) 100%
  );
}

.payment-button-later,
.payment-button-later-verified {
  background: #daedff;
  background: linear-gradient(0deg, darken(#fff, 4%), lighten(#fff, 2%) 100%);
  ///background: #fff;
  border: 2px solid lighten(#e9a23b, 20%);
  border-bottom: 2px solid lighten(#e9a23b, 16%);
  color: #555;
  color: darken(#e9a23b, 12%);
  box-shadow: 0 4px 6px rgba(50, 50, 93, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03);
  text-shadow: none !important;
  cursor: pointer;
}
.payment-button-later:hover {
  color: darken(#e9a23b, 4%);
  background: linear-gradient(0deg, darken(#fff, 4%), lighten(#fff, 2%) 100%);
}

.payment-button-later-verified {
  background: linear-gradient(0deg, darken(#fff, 4%), lighten(#fff, 2%) 100%);
  border: 2px solid lighten(#ff7043, 20%);
  border-bottom: 2px solid lighten(#ff7043, 16%);
  color: darken(#ff7043, 12%);
}
.payment-button-later-verified:hover {
  color: darken(#ff7043, 4%);
  background: linear-gradient(0deg, darken(#fff, 4%), lighten(#fff, 2%) 100%);
}

.payment-button-cancel {
  background: #fff;
  border: 2px solid #fff;
  box-shadow: none !important;
  text-shadow: none !important;
  color: #999;
  border-radius: 8px;
  cursor: pointer;
  //text-decoration: underline;
}
.payment-button-cancel:hover {
  background: #f3f3f3;
  color: #888;
}
.payment-notice {
  padding: 7px 12px 0 14px;
  font-size: 12pt;
  font-weight: 500;
  color: #666;
}

.checkbox-icon {
  font-size: 27pt !important;
}

.option-heading {
  margin: 0 auto;
  font-size: 15pt;
  font-weight: 700;
  text-align: center;
  line-height: 1.5em;
}
.option-icon {
  float: left;
  margin-top: 2px;
  font-size: 16pt !important;
}
.option-label {
  float: left;
  margin: 0px 0 0 4px;
  font-size: 16pt !important;
}

.buttons {
  text-align: center;
  padding: 16px;
}
.continue-button {
  background: #daedff;
  color: #2196f3;
}

.help-icon-i {
  padding-left: 8px;
  font-size: 17pt;
}
.help-icon-i:hover {
  opacity: 0.7;
  cursor: pointer;
}

// Manuscript
#dropzone {
  margin: 18px 50px 6px 40px;
  font-size: 13pt;
  font-family: $font-family;
  border-radius: 4px;
  background: lighten($color-blue, 47%);
  border: 3px solid lighten($color-blue, 41%);
  color: lighten($color-blue, 10%);
}

.manuscript-display {
  text-align: center;
  .manuscript-done {
    margin-top: 48px;
  }
  .manuscript-done-icon {
    font-size: 45pt;
    color: $color-blue;
  }
  .manuscript-done-filetype {
    width: 80px;
    margin: 20px auto;
    margin-top: 10px;
    background: #eee;
    border-radius: 32px;
    padding: 0.3em 1em !important;
    font-size: 13pt;
    font-weight: 700;
    color: $color-blue;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
}
.manuscript-done-more {
  padding: 20px 22px 12px 30px;
  font-size: 12pt !important;
  font-weight: 500;
  color: #111;
}

.upload-instructions {
  font-size: 11.5pt;
  padding: 8px 30px 0 40px;
  color: #111;
  font-weight: 500;
}

.remove-file {
  padding: 0em;
  padding-bottom: 120x;
  text-align: center;
  font-size: 13pt;
  font-weight: 500;
  text-decoration: underline;
  color: $color-blue;
}
.remove-file:hover {
  cursor: pointer;
  color: lighten($color-blue, 10%);
}

// Payment modals
.modal-container {
  position: fixed;
  width: 100%;
  top: 33%;
  left: 0%;
  margin: 0px auto;
  background-color: #fff;
  padding: 30px 40px 40px 310px;
  font-family: "Source Sans Pro";
  box-shadow: 0px 1px 0.5rem rgba(116, 75, 75, 0.45);
}
.modal-container-later {
  position: fixed;
}
.payment-confirm {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;

  .payment-heading {
    font-weight: 400;
    color: #d08417;
    font-size: 24pt;
    line-height: 1.2em;
  }
  .payment-heading-verified {
    color: darken(#ff7043, 4%);
  }
  .payment-line {
    padding: 14px 0;
    font-weight: 500;
    color: #111;
    font-size: 12pt;
  }
}

@media (max-width: 768px) {
  .upgrade-card {
    margin: 20px 6px !important;
  }
  .modal-container {
    height: 80%;
    top: 20%;
    padding: 32px !important;
  }

  .modal-container-later {
    position: fixed;
    height: 80%;
    top: 20%;
  }

  .payment-button-now {
    margin-bottom: 16px !important;
  }
}

@media (max-width: 330px) {
  .modal-container {
    height: 90%;
    top: 10%;
    padding: 32px !important;
  }

  .modal-container-later {
    position: fixed;
    height: 90%;
    top: 10%;
  }
}
</style>